/**
 * Calculation utility functions for WalkCompanion
 */

/**
 * Calculate progress percentage
 */
export function calculateProgress(currentValue: number, targetValue: number): number {
  if (targetValue <= 0) return 0;
  return Math.min((currentValue / targetValue) * 100, 100);
}

/**
 * Calculate progress as decimal (0-1)
 */
export function calculateProgressDecimal(currentValue: number, targetValue: number): number {
  if (targetValue <= 0) return 0;
  return Math.min(currentValue / targetValue, 1);
}

/**
 * Calculate remaining value
 */
export function calculateRemaining(currentValue: number, targetValue: number): number {
  return Math.max(0, targetValue - currentValue);
}

/**
 * Calculate points earned based on progress and challenge duration
 */
export function calculatePointsEarned(progress: number, totalPoints: number, startDate?: string, endDate?: string): number {
  if (progress >= 1) {
    // If challenge has duration, calculate points per day
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const pointsPerDay = Math.round(totalPoints / totalDays);
      return pointsPerDay;
    }
    // Fallback to total points if no duration specified
    return totalPoints;
  }
  return Math.floor(progress * totalPoints);
}

/**
 * Calculate total points earned from daily completions
 */
export function calculateTotalPointsFromDailyCompletions(dailyCompletions: { [date: string]: { points: number } }): number {
  if (!dailyCompletions) return 0;
  return Object.values(dailyCompletions).reduce((total, day) => total + (day.points || 0), 0);
}

/**
 * OPTIMIZED: Get total points earned using stored participation value
 * Use this for reports and analytics instead of recalculating from dailyCompletions
 */
export function getStoredPointsEarned(participation: { pointsEarned?: number }): number {
  return participation.pointsEarned || 0;
}

/**
 * OPTIMIZED: Get challenge progress using stored participation value
 * Use this for reports and analytics instead of recalculating from dailyCompletions
 */
export function getStoredProgress(participation: { progress?: number }): number {
  return participation.progress || 0;
}

/**
 * OPTIMIZED: Get current value using stored participation value
 * Use this for reports and analytics instead of recalculating from dailyCompletions
 */
export function getStoredCurrentValue(participation: { currentValue?: number }): number {
  return participation.currentValue || 0;
}

/**
 * Calculate points earned based on daily completions and challenge data
 */
export function calculatePointsEarnedFromCompletions(
  dailyCompletions: { [date: string]: { isCompleted: boolean; points: number } },
  totalPoints: number,
  startDate: string,
  endDate: string
): number {
  if (!dailyCompletions) return 0;

  // Calculate total days in challenge
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const pointsPerDay = Math.round(totalPoints / totalDays);

  // Sum up points from all completed days
  let totalEarned = 0;
  Object.values(dailyCompletions).forEach(day => {
    if (day.isCompleted) {
      totalEarned += pointsPerDay;
    }
  });

  return totalEarned;
}

/**
 * Calculate daily points earned for a challenge
 */
export function calculateDailyPointsEarned(totalPoints: number, startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return Math.round(totalPoints / totalDays);
}

/**
 * Calculate average from array of numbers
 */
export function calculateAverage(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
}

/**
 * Calculate sum from array of numbers
 */
export function calculateSum(values: number[]): number {
  return values.reduce((acc, val) => acc + val, 0);
}

/**
 * Calculate percentage change
 */
export function calculatePercentageChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return newValue > 0 ? 100 : 0;
  return ((newValue - oldValue) / oldValue) * 100;
}

/**
 * Calculate streak from boolean array
 */
export function calculateStreak(completions: boolean[]): number {
  let streak = 0;
  for (let i = completions.length - 1; i >= 0; i--) {
    if (completions[i]) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
}

/**
 * Calculate longest streak from boolean array
 */
export function calculateLongestStreak(completions: boolean[]): number {
  let currentStreak = 0;
  let longestStreak = 0;

  for (const completion of completions) {
    if (completion) {
      currentStreak++;
      longestStreak = Math.max(longestStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }

  return longestStreak;
}

/**
 * Calculate completion rate
 */
export function calculateCompletionRate(completions: boolean[]): number {
  if (completions.length === 0) return 0;
  const completedCount = completions.filter(c => c).length;
  return (completedCount / completions.length) * 100;
}

/**
 * Calculate distance in different units
 */
export function convertDistance(value: number, fromUnit: string, toUnit: string): number {
  // Convert to meters first
  let meters = 0;

  switch (fromUnit.toLowerCase()) {
    case 'meters':
    case 'm':
      meters = value;
      break;
    case 'kilometers':
    case 'km':
      meters = value * 1000;
      break;
    case 'miles':
    case 'mi':
      meters = value * 1609.34;
      break;
    case 'feet':
    case 'ft':
      meters = value * 0.3048;
      break;
    default:
      return value; // Return original if unit not recognized
  }

  // Convert from meters to target unit
  switch (toUnit.toLowerCase()) {
    case 'meters':
    case 'm':
      return meters;
    case 'kilometers':
    case 'km':
      return meters / 1000;
    case 'miles':
    case 'mi':
      return meters / 1609.34;
    case 'feet':
    case 'ft':
      return meters / 0.3048;
    default:
      return meters; // Return meters if target unit not recognized
  }
}

/**
 * Calculate calories burned from steps (rough estimate)
 */
export function calculateCaloriesFromSteps(steps: number, weightKg: number = 70): number {
  // Rough estimate: 1 step = 0.04 calories for average person
  // This is a simplified calculation - real apps would use more complex formulas
  return Math.round(steps * 0.04 * (weightKg / 70));
}

/**
 * Calculate steps from distance
 */
export function calculateStepsFromDistance(distanceMeters: number, stepLengthMeters: number = 0.762): number {
  // Average step length is about 0.762 meters (30 inches)
  return Math.round(distanceMeters / stepLengthMeters);
}

/**
 * Calculate distance from steps
 */
export function calculateDistanceFromSteps(steps: number, stepLengthMeters: number = 0.762): number {
  return steps * stepLengthMeters;
}

/**
 * Calculate time from seconds
 */
export function formatTimeFromSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

/**
 * Calculate pace (time per unit distance)
 */
export function calculatePace(timeSeconds: number, distanceMeters: number): number {
  if (distanceMeters <= 0) return 0;
  return timeSeconds / distanceMeters; // seconds per meter
}

/**
 * Calculate speed (distance per unit time)
 */
export function calculateSpeed(distanceMeters: number, timeSeconds: number): number {
  if (timeSeconds <= 0) return 0;
  return distanceMeters / timeSeconds; // meters per second
}

/**
 * Round to specified decimal places
 */
export function roundToDecimal(value: number, decimalPlaces: number = 2): number {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculate percentile rank
 */
export function calculatePercentileRank(value: number, values: number[]): number {
  if (values.length === 0) return 0;

  const sortedValues = [...values].sort((a, b) => a - b);
  const rank = sortedValues.findIndex(v => v >= value);

  if (rank === -1) return 100;
  return (rank / values.length) * 100;
}

/**
 * Calculate standard deviation
 */
export function calculateStandardDeviation(values: number[]): number {
  if (values.length === 0) return 0;

  const mean = calculateAverage(values);
  const squaredDifferences = values.map(value => Math.pow(value - mean, 2));
  const variance = calculateAverage(squaredDifferences);

  return Math.sqrt(variance);
}

/**
 * Calculate median
 */
export function calculateMedian(values: number[]): number {
  if (values.length === 0) return 0;

  const sortedValues = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 0) {
    const first = sortedValues[middle - 1];
    const second = sortedValues[middle];
    if (first !== undefined && second !== undefined) {
      return (first + second) / 2;
    }
    return 0;
  } else {
    const value = sortedValues[middle];
    return value !== undefined ? value : 0;
  }
}

/**
 * Calculate mode (most frequent value)
 */
export function calculateMode(values: number[]): number | null {
  if (values.length === 0) return null;

  const frequency: Record<number, number> = {};
  let maxFrequency = 0;
  let mode: number | null = null;

  for (const value of values) {
    frequency[value] = (frequency[value] || 0) + 1;
    const currentFrequency = frequency[value];
    if (currentFrequency && currentFrequency > maxFrequency) {
      maxFrequency = currentFrequency;
      mode = value;
    }
  }

  return mode;
} 